import React, { useEffect, useRef } from 'react';
import { Redirect, RouteProps } from 'react-router';
import { Route } from 'react-router-dom';
import qs from 'query-string';
import { Routes } from 'constants/index';
import { getPath, isServer } from 'utils';
import { getCurrentLocation } from 'utils/routes';
import { getInviteIdSelector } from 'modules/startup/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { getMembershipInviteData } from 'modules/membership/actions';
import {
  membershipLoggedOutInviteDataLoadingSelector,
  membershipLoggedOutInviteDataSelector,
} from 'modules/membership/selectors';
import { Loader } from '../Loader/Loader';
import { getAuthHelper } from 'utils/auth';
import { ConsumerAuthHelper } from 'utils/auth/consumer';
interface Props extends RouteProps {
  isAuth: boolean | null;
  isPublic?: boolean;
  skipRedirect?: boolean;
  isInCimaMembershipPromotionPage?: boolean;
}

export const SecureRoute: React.FC<Props> = ({
  component: Component,
  isAuth,
  isPublic = false,
  skipRedirect = isServer,
  isInCimaMembershipPromotionPage,
  ...routeProps
}) => {
  const dispatch = useDispatch();
  const inviteId = useSelector(getInviteIdSelector);
  const inviteData = useSelector(membershipLoggedOutInviteDataSelector);
  const inviteDataLoading = useSelector(membershipLoggedOutInviteDataLoadingSelector);
  const [requestedInviteId, setRequestedInviteId] = React.useState('');
  const redirectToOktaWidgetIfInviteDataEmailExists = Boolean(inviteData?.email && inviteData?.isAlreadyHaveAccount);
  const hasRedirectedToOktaWidgetRef = useRef<boolean>(false);
  const { location } = routeProps;
  const currentLocation = getCurrentLocation(location);
  const [prevRoute, hashFragment] = currentLocation.split('#');
  const hash = hashFragment ? `#${hashFragment}` : '';

  const inviteIdFromUrl = React.useMemo(() => {
    const parsedQueryParams = qs.parse(location?.search || '');
    const currentInviteId = parsedQueryParams.inviteid || parsedQueryParams.inviteId;
    return typeof currentInviteId === 'string' ? currentInviteId : '';
  }, [location]);

  const effectiveInviteId = inviteIdFromUrl || inviteId;

  // 0. If inviteId exists, get invite data first before rendering anything
  useEffect(() => {
    if (!effectiveInviteId) {
      setRequestedInviteId('');
      return;
    }
    setRequestedInviteId(effectiveInviteId);
    dispatch(getMembershipInviteData(effectiveInviteId));
  }, [dispatch, effectiveInviteId]);

  useEffect(() => {
    if (
      !effectiveInviteId ||
      !redirectToOktaWidgetIfInviteDataEmailExists ||
      hasRedirectedToOktaWidgetRef.current ||
      isAuth
    )
      return;

    // For firm billing invite, if invite data has email and already have account,
    // then redirect to okta widget with email pre-filled
    const authHelper = getAuthHelper();
    if (authHelper instanceof ConsumerAuthHelper) {
      hasRedirectedToOktaWidgetRef.current = true;
      if (!sessionStorage.getItem('okta-post-login-redirect')) {
        sessionStorage.setItem('okta-post-login-redirect', currentLocation || getPath(Routes.HOME));
      }
      authHelper.redirectToHostedLoginWidget({ loginHint: inviteData.email });
    }
  }, [redirectToOktaWidgetIfInviteDataEmailExists, effectiveInviteId, inviteData.email, isAuth, currentLocation]);

  // 1. Render requested route when user authenticated
  if (isAuth || isPublic) {
    return <Route {...routeProps} component={Component} />;
  }

  // Redirect can be skipped optionally, or by SSR
  if (skipRedirect || (isAuth && isInCimaMembershipPromotionPage)) return null;

  // 2. Check if the url has inviteId, and redirect to registration page /okta widget accordingly
  if (effectiveInviteId) {
    const shouldWaitForInviteData = inviteDataLoading || requestedInviteId !== effectiveInviteId;

    if (shouldWaitForInviteData) {
      return <Loader active content="Loading" />;
    }

    if (redirectToOktaWidgetIfInviteDataEmailExists) {
      return <Loader active content="Loading" />;
    }

    const registrationQueryParams = {
      ...qs.parse(location?.search ?? ''),
      inviteId: effectiveInviteId,
    };

    return (
      <Redirect
        to={{
          pathname: getPath(Routes.REGISTRATION),
          search: `?${qs.stringify(registrationQueryParams)}`,
          state: {
            prevRoute: currentLocation,
            hash,
            email: inviteData?.email,
            registrationPrefill: {
              inviteId: effectiveInviteId,
              email: inviteData?.email,
              firstName: inviteData?.firstName,
              lastName: inviteData?.lastName,
            },
          },
          hash,
        }}
      />
    );
  }

  // 3. Redirect to internal login
  return <Redirect to={{ pathname: getPath(Routes.LOGIN), state: { prevRoute, hash }, hash }} />;
};
